/**
 * PSHCanvasLayer - 2D voxel grid renderer for PSH/LNAPL
 *
 * Uses the SAME rendering logic as dissolved plume (PlumeVoxelCanvasLayer):
 * - Convex hull boundary from wells with PSH
 * - IDW interpolation of thickness
 * - Catmull-Rom boundary smoothing for organic curves
 * - Edge feathering for smooth boundaries
 * - Grid-based voxel rendering
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// ============================================================================
// TYPES
// ============================================================================

export interface PSHWell {
  well_id: string;
  lat: number;
  lon: number;
  product_thickness_ft: number;
  depth_to_water_ft?: number;
}

export interface PSHCanvasLayerProps {
  wells: PSHWell[];
  gridResolution?: number;
  edgeFeatherFt?: number;
  baseOpacity?: number;
  visible?: boolean;
  zIndex?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const FT_PER_DEG_LAT = 364000;

// PSH colors - amber/orange gradient (same as PSHVoxelCube)
const PSH_COLORS = {
  core: '#b45309',    // Dark amber (high thickness)
  mid: '#f59e0b',     // Amber (medium)
  outer: '#fcd34d',   // Light yellow (low thickness)
};

// ============================================================================
// HELPER FUNCTIONS (same as PlumeVoxelCanvasLayer)
// ============================================================================

function ftPerDegLon(lat: number): number {
  return FT_PER_DEG_LAT * Math.cos(lat * Math.PI / 180);
}

/**
 * Smooth boundary points using Catmull-Rom spline interpolation.
 * Creates organic, flowing curves instead of sharp angular boundaries.
 */
function smoothBoundaryPoints(
  points: [number, number][],
  iterations: number = 2,
  tension: number = 0.5
): [number, number][] {
  if (points.length < 3) return points;

  let smoothed = [...points];

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: [number, number][] = [];
    const n = smoothed.length;

    for (let i = 0; i < n; i++) {
      const p0 = smoothed[(i - 1 + n) % n];
      const p1 = smoothed[i];
      const p2 = smoothed[(i + 1) % n];
      const p3 = smoothed[(i + 2) % n];

      newPoints.push(p1);

      const t = 0.5;
      const t2 = t * t;
      const t3 = t2 * t;

      const s = (1 - tension) / 2;
      const b0 = -s * t3 + 2 * s * t2 - s * t;
      const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
      const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
      const b3 = s * t3 - s * t2;

      newPoints.push([
        b0 * p0[0] + b1 * p1[0] + b2 * p2[0] + b3 * p3[0],
        b0 * p0[1] + b1 * p1[1] + b2 * p2[1] + b3 * p3[1],
      ]);
    }

    smoothed = newPoints;
  }

  return smoothed;
}

/**
 * Generate convex hull from wells using Graham scan
 */
function convexHull(wells: PSHWell[]): [number, number][] {
  if (wells.length < 3) {
    return wells.map(w => [w.lat, w.lon] as [number, number]);
  }

  const points = wells.map(w => ({ lat: w.lat, lon: w.lon }));

  // Find lowest point (min lat, then min lon)
  let lowest = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].lat < points[lowest].lat ||
        (points[i].lat === points[lowest].lat && points[i].lon < points[lowest].lon)) {
      lowest = i;
    }
  }
  [points[0], points[lowest]] = [points[lowest], points[0]];

  const pivot = points[0];

  // Sort remaining points by polar angle around pivot
  // CRITICAL: slice() returns a new array — must capture the sorted result
  const sorted = points.slice(1).sort((a, b) => {
    const angleA = Math.atan2(a.lat - pivot.lat, a.lon - pivot.lon);
    const angleB = Math.atan2(b.lat - pivot.lat, b.lon - pivot.lon);
    return angleA - angleB;
  });

  // Graham scan on properly sorted points
  const cross = (o: typeof pivot, a: typeof pivot, b: typeof pivot) =>
    (a.lon - o.lon) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lon - o.lon);

  const hull: typeof points = [pivot, sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    while (hull.length > 1 && cross(hull[hull.length - 2], hull[hull.length - 1], sorted[i]) <= 0) {
      hull.pop();
    }
    hull.push(sorted[i]);
  }

  return hull.map(p => [p.lat, p.lon] as [number, number]);
}

/**
 * Expand boundary outward by a fixed distance (buffer)
 */
function expandBoundary(boundary: [number, number][], bufferFt: number): [number, number][] {
  if (boundary.length < 3) return boundary;

  const centerLat = boundary.reduce((sum, p) => sum + p[0], 0) / boundary.length;
  const centerLon = boundary.reduce((sum, p) => sum + p[1], 0) / boundary.length;

  const lonFactor = ftPerDegLon(centerLat);

  return boundary.map(([lat, lon]) => {
    const dLat = lat - centerLat;
    const dLon = lon - centerLon;
    const distFt = Math.sqrt((dLat * FT_PER_DEG_LAT) ** 2 + (dLon * lonFactor) ** 2);

    if (distFt < 1) return [lat, lon] as [number, number];

    const scale = (distFt + bufferFt) / distFt;
    return [
      centerLat + dLat * scale,
      centerLon + dLon * scale,
    ] as [number, number];
  });
}

/**
 * Check if a point is inside a polygon using ray casting
 */
function isInsidePolygon(lat: number, lon: number, boundary: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [yi, xi] = boundary[i];
    const [yj, xj] = boundary[j];
    if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Calculate distance from a point to the polygon boundary (in feet)
 */
function distanceToPolygonEdge(
  lat: number,
  lon: number,
  boundary: [number, number][],
  centerLat: number
): number {
  const lonFactor = ftPerDegLon(centerLat);
  let minDist = Infinity;

  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [y1, x1] = boundary[i];
    const [y2, x2] = boundary[j];

    const px = lon * lonFactor;
    const py = lat * FT_PER_DEG_LAT;
    const ax = x1 * lonFactor;
    const ay = y1 * FT_PER_DEG_LAT;
    const bx = x2 * lonFactor;
    const by = y2 * FT_PER_DEG_LAT;

    const abx = bx - ax;
    const aby = by - ay;
    const abLen2 = abx * abx + aby * aby;

    if (abLen2 < 0.001) {
      const dx = px - ax;
      const dy = py - ay;
      minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
      continue;
    }

    const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / abLen2));
    const closestX = ax + t * abx;
    const closestY = ay + t * aby;

    const dx = px - closestX;
    const dy = py - closestY;
    minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
  }

  return minDist;
}

/**
 * IDW interpolation of PSH thickness from well data
 */
function interpolateThickness(
  lat: number,
  lon: number,
  wells: PSHWell[],
  centerLat: number
): number {
  if (wells.length === 0) return 0;

  const lonFactor = ftPerDegLon(centerLat);
  let sumWeights = 0;
  let sumWeightedThickness = 0;
  const power = 2;

  for (const well of wells) {
    const dLat = (lat - well.lat) * FT_PER_DEG_LAT;
    const dLon = (lon - well.lon) * lonFactor;
    const dist = Math.sqrt(dLat * dLat + dLon * dLon);

    if (dist < 1) return well.product_thickness_ft;

    const weight = 1 / Math.pow(dist, power);
    sumWeights += weight;
    sumWeightedThickness += weight * well.product_thickness_ft;
  }

  return sumWeights > 0 ? sumWeightedThickness / sumWeights : 0;
}

/**
 * Get thickness ratio (linear scale, unlike dissolved plume which uses log)
 */
function getThicknessRatio(thickness: number, maxThickness: number): number {
  if (maxThickness <= 0) return 0;
  return Math.min(1, Math.max(0, thickness / maxThickness));
}

/**
 * Get PSH color based on thickness ratio
 */
function getThicknessColor(ratio: number): { r: number; g: number; b: number } {
  // Parse colors
  const core = { r: 180, g: 83, b: 9 };    // #b45309
  const mid = { r: 245, g: 158, b: 11 };   // #f59e0b
  const outer = { r: 252, g: 211, b: 77 }; // #fcd34d

  if (ratio > 0.6) {
    const t = (ratio - 0.6) / 0.4;
    return {
      r: Math.round(mid.r + (core.r - mid.r) * t),
      g: Math.round(mid.g + (core.g - mid.g) * t),
      b: Math.round(mid.b + (core.b - mid.b) * t),
    };
  } else if (ratio > 0.3) {
    const t = (ratio - 0.3) / 0.3;
    return {
      r: Math.round(outer.r + (mid.r - outer.r) * t),
      g: Math.round(outer.g + (mid.g - outer.g) * t),
      b: Math.round(outer.b + (mid.b - outer.b) * t),
    };
  } else {
    return outer;
  }
}

/**
 * Get opacity based on thickness ratio (same formula as dissolved plume)
 */
function getThicknessOpacity(ratio: number): number {
  return 0.4 + ratio * 0.55;
}

// ============================================================================
// VOXEL GRID GENERATION
// ============================================================================

interface Voxel2D {
  lat: number;
  lon: number;
  r: number;
  g: number;
  b: number;
  opacity: number;
}

/**
 * Generate a circular boundary around a single well or between two wells.
 * Returns 36-point polygon suitable for smoothing and grid generation.
 */
function generateCircleBoundary(
  centerLat: number,
  centerLon: number,
  radiusFt: number,
  numPoints: number = 36
): [number, number][] {
  const lonFactor = ftPerDegLon(centerLat);
  const boundary: [number, number][] = [];
  for (let i = 0; i < numPoints; i++) {
    const angle = (2 * Math.PI * i) / numPoints;
    const dLat = (radiusFt * Math.sin(angle)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(angle)) / lonFactor;
    boundary.push([centerLat + dLat, centerLon + dLon]);
  }
  return boundary;
}

/**
 * Generate a capsule (stadium) boundary between two wells.
 * Creates semicircles at each well connected by tangent lines.
 */
function generateCapsuleBoundary(
  well1: PSHWell,
  well2: PSHWell,
  radiusFt: number,
  numPoints: number = 36
): [number, number][] {
  const centerLat = (well1.lat + well2.lat) / 2;
  const lonFactor = ftPerDegLon(centerLat);

  // Direction vector from well1 to well2
  const dx = (well2.lon - well1.lon) * lonFactor;
  const dy = (well2.lat - well1.lat) * FT_PER_DEG_LAT;
  const angle = Math.atan2(dy, dx);

  const boundary: [number, number][] = [];
  const halfPoints = Math.floor(numPoints / 2);

  // Semicircle around well2 (front)
  for (let i = 0; i <= halfPoints; i++) {
    const a = angle - Math.PI / 2 + (Math.PI * i) / halfPoints;
    const dLat = (radiusFt * Math.sin(a)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(a)) / lonFactor;
    boundary.push([well2.lat + dLat, well2.lon + dLon]);
  }
  // Semicircle around well1 (back)
  for (let i = 0; i <= halfPoints; i++) {
    const a = angle + Math.PI / 2 + (Math.PI * i) / halfPoints;
    const dLat = (radiusFt * Math.sin(a)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(a)) / lonFactor;
    boundary.push([well1.lat + dLat, well1.lon + dLon]);
  }

  return boundary;
}

function generatePSHVoxelGrid(
  wells: PSHWell[],
  gridResolution: number,
  edgeFeatherFt: number
): Voxel2D[] {
  if (wells.length === 0) return [];

  const maxThickness = Math.max(...wells.map(w => w.product_thickness_ft));
  if (maxThickness <= 0) return [];

  // Generate boundary based on well count
  let boundary: [number, number][];

  if (wells.length === 1) {
    // Single well: 50ft radius circle
    boundary = generateCircleBoundary(wells[0].lat, wells[0].lon, 50);
  } else if (wells.length === 2) {
    // Two wells: capsule shape connecting them with 50ft radius
    boundary = generateCapsuleBoundary(wells[0], wells[1], 50);
  } else {
    // 3+ wells: convex hull → expand → smooth
    const rawBoundary = convexHull(wells);
    const expandedBoundary = expandBoundary(rawBoundary, 50);
    boundary = smoothBoundaryPoints(expandedBoundary, 3, 0.5);
  }

  const voxels: Voxel2D[] = [];

  // Calculate bounds
  const lats = boundary.map(p => p[0]);
  const lons = boundary.map(p => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const centerLat = (minLat + maxLat) / 2;
  const latStep = (maxLat - minLat) / gridResolution;
  const lonStep = (maxLon - minLon) / gridResolution;

  // Skip degenerate bounds
  if (latStep <= 0 || lonStep <= 0) return [];

  for (let i = 0; i <= gridResolution; i++) {
    for (let j = 0; j <= gridResolution; j++) {
      const lat = minLat + i * latStep;
      const lon = minLon + j * lonStep;

      if (isInsidePolygon(lat, lon, boundary)) {
        const thickness = interpolateThickness(lat, lon, wells, centerLat);
        const ratio = getThicknessRatio(thickness, maxThickness);

        // Skip very low thickness cells
        if (ratio < 0.02) continue;

        // Edge feathering - fade out near polygon boundary
        const edgeDist = distanceToPolygonEdge(lat, lon, boundary, centerLat);
        const edgeFactor = Math.min(1, edgeDist / edgeFeatherFt);
        // Smoothstep for natural falloff
        const smoothEdgeFactor = edgeFactor * edgeFactor * (3 - 2 * edgeFactor);

        const color = getThicknessColor(ratio);
        const baseOpacity = getThicknessOpacity(ratio);

        voxels.push({
          lat,
          lon,
          r: color.r,
          g: color.g,
          b: color.b,
          opacity: baseOpacity * smoothEdgeFactor,
        });
      }
    }
  }

  return voxels;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PSHCanvasLayer({
  wells,
  gridResolution = 160,
  edgeFeatherFt = 30,
  baseOpacity = 1,
  visible = true,
  zIndex = 450,
}: PSHCanvasLayerProps) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pre-generate voxels (supports 1+ wells)
  const voxels = useMemo(() => {
    if (!visible || wells.length === 0) return [];
    return generatePSHVoxelGrid(wells, gridResolution, edgeFeatherFt);
  }, [wells, gridResolution, edgeFeatherFt, visible]);

  // Create canvas once inside a custom Leaflet pane (below markerPane z:600, above overlayPane z:400)
  // Canvas persists across visibility toggles — we toggle display instead of destroying DOM.
  useEffect(() => {
    if (!map) return;

    // Create custom pane if it doesn't exist (PSH slightly above dissolved plume)
    const paneName = 'pshCanvasPane';
    if (!map.getPane(paneName)) {
      const pane = map.createPane(paneName);
      pane.style.zIndex = '455';
      pane.style.pointerEvents = 'none';
    }
    const pane = map.getPane(paneName)!;

    const container = L.DomUtil.create('div', 'psh-canvas-container');
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'hidden';

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    container.appendChild(canvas);

    pane.appendChild(container);

    canvasRef.current = canvas;
    containerRef.current = container;

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      canvasRef.current = null;
      containerRef.current = null;
    };
  }, [map]);

  // Toggle visibility via CSS display instead of DOM destroy/recreate
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.display = visible ? '' : 'none';
    }
  }, [visible]);

  // Calculate plume bounds for consistent cell sizing (like dissolved plume)
  const plumeBounds = useMemo(() => {
    if (wells.length === 0) return null;
    const lats = wells.map(w => w.lat);
    const lons = wells.map(w => w.lon);
    // For 1-2 wells, add a small buffer so bounds aren't degenerate
    const bufferDeg = wells.length < 3 ? 50 / FT_PER_DEG_LAT : 0;
    return {
      minLat: Math.min(...lats) - bufferDeg,
      maxLat: Math.max(...lats) + bufferDeg,
      minLon: Math.min(...lons) - bufferDeg,
      maxLon: Math.max(...lons) + bufferDeg,
    };
  }, [wells]);

  // Draw voxels to canvas
  const drawVoxels = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !map || !visible || voxels.length === 0 || !plumeBounds) {
      return;
    }

    const size = map.getSize();
    const bounds = map.getBounds();

    // Offset container within the pane to align with the viewport
    const layerOffset = map.containerPointToLayerPoint(L.point(0, 0));
    container.style.transform = `translate(${layerOffset.x}px, ${layerOffset.y}px)`;
    container.style.width = `${size.x}px`;
    container.style.height = `${size.y}px`;

    canvas.width = size.x;
    canvas.height = size.y;
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate cell size based on PLUME bounds (not view bounds) - same as dissolved plume
    const zoom = map.getZoom();
    const metersPerPixel = 156543.03 * Math.cos(bounds.getCenter().lat * Math.PI / 180) / Math.pow(2, zoom);

    const latRange = plumeBounds.maxLat - plumeBounds.minLat;
    const lonRange = plumeBounds.maxLon - plumeBounds.minLon;
    const avgRangeDeg = (latRange + lonRange) / 2;
    const cellSizeDeg = avgRangeDeg / gridResolution;
    const cellSizeMeters = cellSizeDeg * 111320; // Approx meters per degree
    const cellSizePixels = Math.max(2, Math.min(20, cellSizeMeters / metersPerPixel * 1.2));

    // Visible bounds with buffer
    const latBuffer = (bounds.getNorth() - bounds.getSouth()) * 0.1;
    const lonBuffer = (bounds.getEast() - bounds.getWest()) * 0.1;
    const visibleBounds = {
      north: bounds.getNorth() + latBuffer,
      south: bounds.getSouth() - latBuffer,
      east: bounds.getEast() + lonBuffer,
      west: bounds.getWest() - lonBuffer,
    };

    // Pre-compute affine transform: lat/lon → pixel using two reference points
    // This avoids calling map.latLngToContainerPoint() per voxel
    const refSW = map.latLngToContainerPoint([visibleBounds.south, visibleBounds.west]);
    const refNE = map.latLngToContainerPoint([visibleBounds.north, visibleBounds.east]);
    const viewLatRange = visibleBounds.north - visibleBounds.south;
    const viewLonRange = visibleBounds.east - visibleBounds.west;
    const pxPerLat = (refNE.y - refSW.y) / viewLatRange;  // negative (north is up)
    const pxPerLon = (refNE.x - refSW.x) / viewLonRange;
    const originX = refSW.x;
    const originY = refSW.y;
    const originLat = visibleBounds.south;
    const originLon = visibleBounds.west;

    // Draw voxels using affine transform
    let drawnCount = 0;
    for (const voxel of voxels) {
      // Skip voxels outside visible area
      if (
        voxel.lat < visibleBounds.south ||
        voxel.lat > visibleBounds.north ||
        voxel.lon < visibleBounds.west ||
        voxel.lon > visibleBounds.east
      ) {
        continue;
      }

      const px = originX + (voxel.lon - originLon) * pxPerLon;
      const py = originY + (voxel.lat - originLat) * pxPerLat;

      // Set fill style
      ctx.fillStyle = `rgba(${voxel.r}, ${voxel.g}, ${voxel.b}, ${voxel.opacity * baseOpacity})`;

      // Draw filled rectangle (centered on point)
      const halfSize = cellSizePixels / 2;
      ctx.fillRect(px - halfSize, py - halfSize, cellSizePixels, cellSizePixels);

      drawnCount++;
    }

  }, [map, voxels, visible, baseOpacity, gridResolution, plumeBounds]);

  // Redraw on map events — only on moveend to avoid per-pixel redraws
  useMapEvents({
    moveend: drawVoxels,
    zoomend: drawVoxels,
    resize: drawVoxels,
  });

  // Initial draw, redraw on voxels change, and handle visibility toggle.
  // Single effect to avoid double drawVoxels when visibility changes.
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(drawVoxels, 50);
      return () => clearTimeout(timer);
    } else if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [drawVoxels, voxels, visible]);

  return null;
}

export default PSHCanvasLayer;
